<template>
  <div class="convey-connect-dashboard d-flex flex-column">
    <div class="d-flex align-items-center justify-content-between flex-wrap mb-3 gap-3">
      <div class="d-flex align-items-center gap-3">
        <h4 class="convey-connect-dashboard--page-title mb-0 ">
          Convey Connect Dashboard
        </h4>
        <DashboardToggle @showDashboard="showDashboard" />
      </div>
      <ConveyDatePicker
        v-if="userDashboardView"
        placeholder="Select Date Range"
        @changeDatePicker="handleChangeDatePicker"
        :isDarkMode="isDarkMode"
        :currentDateRange="dateRange"
      />
    </div>

    <b-overlay
      :show="isLoading || isRecentCallsLoading || isGeoMapLoading"
      rounded="sm"
      no-wrap
    ></b-overlay>
    <div v-if="userDashboardView" class="convey-connect-dashboard--blocks row d-flex">
      <div
        class="col-md-12 col-xl-6 convey-connect-dashboard--statistics-div"
      >
        <Statistics :statsCount="statsCount" :isDarkMode="isDarkMode" />
      </div>
      <div
        class="col-md-12 col-xl-6 mb-3 convey-connect-dashboard--conversation-graph-div"
        style="height: 388px"
      >
        <CallActivity
          :callActivityData="callActivityData"
          :isDarkMode="isDarkMode"
        />
      </div>
      <div
        class="col-md-12 col-xl-6 mb-3 convey-connect-dashboard--statistics-div"
      >
        <CallStatus :isDarkMode="isDarkMode" :callStatusData="callStatusData" />
      </div>
      <div
        class="col-md-12 col-xl-6 mb-3 convey-connect-dashboard--statistics-div"
      >
        <ServiceUsage
          :serviceUsageData="serviceUsageData"
          :isDarkMode="isDarkMode"
        />
      </div>

      <div class="col-md-12 col-sm-12 col-lg-4 col-xl-3 mb-3">
        <ClientDeviceType
          :clientDeviceTypeData="clientDeviceTypeData"
          :isDarkMode="isDarkMode"
        />
      </div>
      <div class="col-md-12 col-sm-12 col-lg-8 col-xl-9 mb-3">
        <RecentCalls
          :recentCallsData="recentCallsData"
          :isDarkMode="isDarkMode"
        />
      </div>
    </div>
    <div v-else class="convey-connect-dashboard--simplified d-flex align-items-center justify-content-center px-3 py-3">
      <img
        class="app-logo"
        :src="agencyLogo"
        alt="Convey911 Agency Logo"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Statistics from "./Statistics.vue";
import CallActivity from "./CallActivity.vue";
import ClientDeviceType from "./client-device-type/ClientDeviceType.vue";
import ConveyDatePicker from "../../common/DatePicker.vue";
import moment from "moment";
import { mapGetters } from "vuex";
import { runGraphqlQuery } from "../../../graphql/utils";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import {
  recentCallsquery,
  conveyConnectStatsQuery,
} from "../../../graphql/queries";
import CallStatus from "./CallStatus.vue";
import ServiceUsage from "./ServiceUsage.vue";
import RecentCalls from "./RecentCalls.vue";
import DashboardToggle from "../../common/DashboardToggle.vue";

export default {
  name: "ConveyConnectDashboard",
  mixins: [formatter, errorHandler],
  components: {
    Statistics,
    CallActivity,
    CallStatus,
    ClientDeviceType,
    ServiceUsage,
    ConveyDatePicker,
    RecentCalls,
    DashboardToggle
  },
  data() {
    return {
      dateRange: {
        start_date: moment().subtract(29, "days").format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
      },
      statsCount: {
        totalDuration: null,
        totalCalls: null,
        accountCharges: null,
        callQualityRating: 0,
        requesterRating: null,
        interpreterRating: null,
      },
      callActivityData: null,
      callStatusData: [],
      serviceUsageData: [],
      clientDeviceTypeData: null,
      recentCallsData: [],
      recentCallsOptions: {
        perPage: 10,
        query: "",
        orderBy: "record_timestamp",
        sortDesc: true,
        currentPage: 1,
      },

      isLoading: false,
      isGeoMapLoading: false,
      isRecentCallsLoading: false,
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken", "isDarkMode", "userDashboardView"]),
    ...mapGetters("agency", ["currentAgencyId", "agencyLogo"]),
  },
  created() {
    if (this.userDashboardView) {
      this.fetchOperations();
    }
  },
  watch: {
    dateRange(newVal, oldVal) {
      if (newVal != oldVal) {
        this.fetchOperations();
      }
    },
  },
  methods: {
    showDashboard(value) {
      if (value) {
        this.fetchOperations();
      } else {
        this.abortFetchOperations();
      }
    },
    handleChangeDatePicker(data) {
      if (data.start && data.end) {
        this.dateRange = {
          start_date: data.start,
          end_date: data.end,
        };
      } else {
        this.dateRange = null;
      }
    },
    fetchOperations() {
      this.fetchAbortController = new AbortController();
      this.fetchAllData();
      this.getRecentCallsData();
    },
    abortFetchOperations() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    async fetchAllData() {
      try {
        this.isLoading = true;
        let startDate = moment().startOf("year").format("YYYY-MM-DD");
        let endDate = moment().endOf("year").format("YYYY-MM-DD");

        if (this.dateRange && this.dateRange.start_date) {
          startDate = this.dateRange.start_date;
          endDate = this.dateRange.end_date;
        }

        const query = conveyConnectStatsQuery(startDate, endDate);
        const res = await runGraphqlQuery(query, this.fetchAbortController.signal);
        const { getConveyConnectStats } = res.data.data;
        const responseData = getConveyConnectStats;
        this.callStatusData = responseData.stats.callStatusChartData;
        this.statsCount = {
          accountCharges: {
            totalCount: this.formatNumber(
              responseData.stats.accountCharges.total
            ),
            audioCount: this.formatNumber(
              responseData.stats.accountCharges.audio
            ),
            videoCount: this.formatNumber(
              responseData.stats.accountCharges.video
            ),
          },
          totalDuration: {
            total: this.formatDuration(
              responseData.stats.durationStats.totalDuration
            ),
            average: this.formatDuration(
              responseData.stats.durationStats.averageDuration
            ),
            averageQueTime: this.formatDuration(
              responseData.stats.durationStats.averageQueueTime
            ),
          },
          totalCalls: {
            totalCount: this.formatNumber(
              responseData.stats.callsCount.total
            ),
            audioCount: this.formatNumber(
              responseData.stats.callsCount.audio
            ),
            videoCount: this.formatNumber(
              responseData.stats.callsCount.video
            ),
          },
          callQualityRating: this.formatNumber(
            responseData.stats.callQualityRating
          ),
          requesterRating: {
            total: this.formatNumber(responseData.stats.requesterRating),
            graphData: responseData.stats.requesterRatingGraph,
            average: null,
          },
          interpreterRating: {
            total: this.formatNumber(responseData.stats.interpreterRating),
            graphData: responseData.stats.interpreterRatingGraph,
            average: null,
          },
        };
        this.callActivityData = {
          total: this.formatNumber(responseData.stats.callsCount.total),
          graphData: responseData.callActivityGraphData,
          average: this.formatDuration(
            responseData.stats.durationStats.averageDuration
          ),
        };
        this.serviceUsageData = responseData.stats.servicesChartData;
        this.clientDeviceTypeData = responseData.stats.userAgents;
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[ConveyConnectDashboard:fetchAllData] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async getRecentCallsData() {
      try {
        this.isRecentCallsLoading = true;
        let startDate = moment().startOf("year").format("YYYY-MM-DD");
        let endDate = moment().endOf("year").format("YYYY-MM-DD");

        if (this.dateRange && this.dateRange.start_date) {
          startDate = this.dateRange.start_date;
          endDate = this.dateRange.end_date;
        }

        const offset =
          (this.recentCallsOptions.currentPage - 1) *
          this.recentCallsOptions.perPage;
        const query = recentCallsquery({
          startDate,
          endDate,
          offset,
          orderBy: this.recentCallsOptions.orderBy,
          sortDesc: this.recentCallsOptions.sortDesc ? "DESC" : "ASC",
          perPage: this.recentCallsOptions.perPage,
          searchKey: "",
          filter: []
        });

        const res = await runGraphqlQuery(query, this.fetchAbortController.signal);
        const responseData = res.data.data.getConveyConnectCallHistory;
        this.recentCallsData = responseData.data;
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[ConveyConnectDashboard:getRecentCallData] ERROR: ", err);
        }
      } finally {
        this.isRecentCallsLoading = false;
      }
    },
  },
  beforeDestroy() {
    this.abortFetchOperations();
  },
};
</script>

<style lang="scss">
.convey-connect-dashboard {
  height: 100%;
  &--blocks {
    &.row {
      margin: 0 -0.5rem !important;
      > * {
        padding: 0 0.5rem !important;
      }
    }
  }
  &--simplified {
    flex: 1;
  }
}
</style>
